import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { Search as SearchIcon, FileText, Calendar, Tag, Briefcase, Wrench, ShoppingBag, Loader2, AlertCircle } from "lucide-react";
import { useSEO } from "@/hooks/use-seo";
import { useTown } from "@/hooks/use-town";

type Hit = { id: number; title: string; sub?: string; meta?: string; href: string };

type SectionKey = "stores" | "businesses" | "deals" | "events" | "news" | "jobs";

type Results = Record<SectionKey, Hit[]>;
type SectionErrors = Record<SectionKey, boolean>;

const SECTION_META: { key: SectionKey; label: string; icon: React.ReactNode; color: string }[] = [
  { key: "stores",     label: "Order Online", icon: <ShoppingBag size={14} />, color: "text-orange-700 bg-orange-50 dark:bg-orange-900/20" },
  { key: "businesses", label: "Businesses",   icon: <Briefcase size={14} />,   color: "text-emerald-700 bg-emerald-50" },
  { key: "deals",      label: "Deals",        icon: <Tag size={14} />,         color: "text-amber-700 bg-amber-50 dark:bg-amber-900/20" },
  { key: "events",     label: "Events",       icon: <Calendar size={14} />,    color: "text-violet-700 bg-violet-50" },
  { key: "news",       label: "News",         icon: <FileText size={14} />,    color: "text-blue-700 bg-blue-50 dark:bg-blue-900/20" },
  { key: "jobs",       label: "Jobs",         icon: <Wrench size={14} />,      color: "text-red-700 bg-red-50" },
];

const EMPTY: Results = { stores: [], businesses: [], deals: [], events: [], news: [], jobs: [] };
const NO_ERRORS: SectionErrors = { stores: false, businesses: false, deals: false, events: false, news: false, jobs: false };

type NewsItem     = { id: number; title: string; summary?: string | null; category?: string | null };
type BusinessItem = { id: number; name: string; description?: string | null; category?: string | null; town?: string | null; fromOrderModule?: boolean; orderStoreSlug?: string | null };
type DealItem     = { id: number; slug?: string | null; title: string; businessName?: string | null; category?: string | null };
type EventItem    = { id: number; slug?: string | null; name: string; eventDate?: string | null; venue?: string | null };
type JobItem      = { id: number; title: string; town?: string | null; category?: string | null };
type StoreItem    = { id: number; slug?: string | null; name: string; description?: string | null; category?: string | null; town?: string | null; isOpen?: boolean };

/** Fetch JSON, returning null (not a throw) when the request or response fails. */
async function getJson<T>(url: string): Promise<{ items: T[] } | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const body = await res.json();
    if (!body || !Array.isArray(body.items)) return null;
    return body as { items: T[] };
  } catch {
    return null;
  }
}

async function fetchAll(q: string, town: string | undefined): Promise<{ results: Results; errors: SectionErrors }> {
  const qs = encodeURIComponent(q);
  const townQs = town ? `&town=${encodeURIComponent(town)}` : "";

  const [news, businesses, deals, events, jobs, stores] = await Promise.all([
    getJson<NewsItem>(`/api/news?search=${qs}${townQs}&limit=5`),
    getJson<BusinessItem>(`/api/businesses?search=${qs}${townQs}&limit=10`),
    getJson<DealItem>(`/api/deals?search=${qs}${townQs}&limit=5`),
    getJson<EventItem>(`/api/events?search=${qs}${townQs}&limit=5`),
    // /api/jobs still applies its search filter after pagination — over-fetch and slice.
    getJson<JobItem>(`/api/jobs?search=${qs}${townQs}&limit=100`),
    getJson<StoreItem>(`/api/order/stores?search=${qs}${townQs}&limit=5`),
  ]);

  const storeItems = stores?.items ?? [];
  const storeIds = new Set(storeItems.map(s => s.id));

  const results: Results = {
    stores: storeItems.slice(0, 5).map(s => ({
      id: s.id,
      title: s.name,
      sub: s.description ?? undefined,
      meta: [s.category, s.town, s.isOpen === false ? "Closed" : s.isOpen === true ? "Open now" : null].filter(Boolean).join(" · "),
      href: `/order/${s.slug ?? s.id}`,
    })),
    businesses: (businesses?.items ?? [])
      // Order-module entries already shown under "Order Online" — don't list them twice.
      .filter(b => !(b.fromOrderModule && storeIds.has(b.id)))
      .slice(0, 5)
      .map(b => ({
        id: b.id,
        title: b.name,
        sub: b.description ?? undefined,
        meta: [b.category, b.town].filter(Boolean).join(" · "),
        href: b.fromOrderModule ? `/order/${b.orderStoreSlug ?? b.id}` : `/businesses/${b.id}`,
      })),
    deals: (deals?.items ?? []).slice(0, 5).map(d => ({ id: d.id, title: d.title, sub: d.businessName ?? undefined, meta: d.category ?? undefined, href: `/deals/${d.slug ?? d.id}` })),
    events: (events?.items ?? []).slice(0, 5).map(e => ({ id: e.id, title: e.name, meta: [e.eventDate, e.venue].filter(Boolean).join(" · "), href: `/events/${e.slug ?? e.id}` })),
    news: (news?.items ?? []).slice(0, 5).map(n => ({ id: n.id, title: n.title, sub: n.summary ?? undefined, meta: n.category ?? undefined, href: `/news/${n.id}` })),
    jobs: (jobs?.items ?? []).slice(0, 5).map(j => ({ id: j.id, title: j.title, meta: [j.category, j.town].filter(Boolean).join(" · "), href: `/jobs/${j.id}` })),
  };

  const errors: SectionErrors = {
    stores: stores === null,
    businesses: businesses === null,
    deals: deals === null,
    events: events === null,
    news: news === null,
    jobs: jobs === null,
  };

  return { results, errors };
}

export default function SearchPage() {
  useSEO({ title: "Search — Ayrshire Hub", description: "Search for takeaways, businesses, deals, events, news and jobs across Ayrshire." });

  const { selectedTown, townParam } = useTown();
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.search).get("q") ?? "");
  const [results, setResults] = useState<Results>(EMPTY);
  const [errors, setErrors] = useState<SectionErrors>(NO_ERRORS);
  const [loading, setLoading] = useState(false);
  /** When true the town scope is ignored and results cover all of Ayrshire. */
  const [searchAllAyrshire, setSearchAllAyrshire] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeq = useRef(0);

  const townScope = searchAllAyrshire ? undefined : townParam;
  const isTownScoped = !searchAllAyrshire && !!townParam && selectedTown !== "All Towns";

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      // Invalidate any in-flight request so a late response can't repopulate the list.
      requestSeq.current++;
      setResults(EMPTY);
      setErrors(NO_ERRORS);
      setLoading(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const seq = ++requestSeq.current;
      setLoading(true);
      try {
        const { results: r, errors: e } = await fetchAll(q, townScope);
        // Ignore responses from a superseded keystroke.
        if (seq !== requestSeq.current) return;
        setResults(r);
        setErrors(e);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, townScope]);

  const totalHits = SECTION_META.reduce((acc, s) => acc + results[s.key].length, 0);
  const failedSections = SECTION_META.filter(s => errors[s.key]);
  const allFailed = failedSections.length === SECTION_META.length;
  const hasQuery = query.trim().length >= 2;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <h1 className="text-3xl font-bold text-primary mb-5">Search</h1>
          <div className="relative">
            <SearchIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search takeaways, businesses, deals, events, news, jobs…"
              className="w-full pl-11 pr-4 py-3.5 border border-slate-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white shadow-sm"
              data-testid="input-search"
            />
            {loading && <Loader2 size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
          </div>
          {isTownScoped && (
            <p className="text-xs text-slate-500 mt-3">
              Showing results near <span className="font-semibold text-slate-700">{selectedTown}</span>
              {" · "}
              <button
                type="button"
                onClick={() => setSearchAllAyrshire(true)}
                className="text-primary font-semibold hover:underline"
                data-testid="button-search-all-ayrshire"
              >
                Search all of Ayrshire
              </button>
            </p>
          )}
          {searchAllAyrshire && selectedTown !== "All Towns" && (
            <p className="text-xs text-slate-500 mt-3">
              Showing results across all of Ayrshire
              {" · "}
              <button
                type="button"
                onClick={() => setSearchAllAyrshire(false)}
                className="text-primary font-semibold hover:underline"
                data-testid="button-search-near-town"
              >
                Back to {selectedTown}
              </button>
            </p>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {!hasQuery ? (
          <div className="text-center py-20">
            <SearchIcon size={40} className="text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Type at least 2 characters to search</p>
          </div>
        ) : loading && totalHits === 0 ? (
          <div className="text-center py-20">
            <Loader2 size={32} className="text-primary mx-auto mb-3 animate-spin" />
            <p className="text-slate-400 text-sm">Searching…</p>
          </div>
        ) : allFailed ? (
          <div className="text-center py-20" data-testid="text-search-error">
            <AlertCircle size={40} className="text-red-300 mx-auto mb-3" />
            <p className="text-slate-600 font-semibold">Search is unavailable right now</p>
            <p className="text-slate-400 text-sm mt-1">Check your connection and try again in a moment</p>
          </div>
        ) : totalHits === 0 ? (
          <div className="text-center py-20">
            <SearchIcon size={40} className="text-slate-200 mx-auto mb-3" />
            <p className="text-slate-600 font-semibold">No results for "{query}"</p>
            <p className="text-slate-400 text-sm mt-1">
              {isTownScoped ? `Nothing near ${selectedTown} — try widening your search` : "Try a different search term"}
            </p>
            {isTownScoped && (
              <button
                type="button"
                onClick={() => setSearchAllAyrshire(true)}
                className="mt-4 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:opacity-90"
                data-testid="button-widen-search"
              >
                Search all of Ayrshire
              </button>
            )}
            {failedSections.length > 0 && (
              <p className="text-xs text-amber-600 mt-4">
                {failedSections.map(s => s.label).join(", ")} could not be searched
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            <p className="text-sm text-slate-500">{totalHits} result{totalHits !== 1 ? "s" : ""} for "{query}"</p>
            {SECTION_META.map(({ key, label, icon, color }) => {
              const hits = results[key];
              const failed = errors[key];
              if (!hits.length && !failed) return null;
              return (
                <div key={key}>
                  <div className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full mb-3 ${color}`}>
                    {icon}
                    {label.toUpperCase()}
                  </div>
                  {failed ? (
                    <div className="flex items-center gap-2 bg-white rounded-xl border border-amber-100 px-4 py-3 text-sm text-amber-700" data-testid={`text-error-${key}`}>
                      <AlertCircle size={15} />
                      Couldn't load {label.toLowerCase()} results
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {hits.map(hit => (
                        <Link key={hit.id} href={hit.href} className="block bg-white rounded-xl border border-slate-100 shadow-sm px-4 py-3 hover:shadow-md hover:border-primary/20 transition-all group">
                          <p className="font-semibold text-slate-800 group-hover:text-primary transition-colors line-clamp-1">{hit.title}</p>
                          {hit.sub && <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">{hit.sub}</p>}
                          {hit.meta && <p className="text-xs text-slate-400 mt-0.5">{hit.meta}</p>}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
